import React, { FC, useContext, useTransition } from 'react';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import { Preview3DStoreContext } from 'src/components/widgets/preview3D';
import styled from 'styled-components';
import { ErrorMessage } from '../../../../shared/globals';
import { T } from '../../../../shared/helpers';
import NotFoundIcon from '../../../../shared/icons/icon-not-found.svg';
import { BackButton } from '../../../widgets/buttons';
import { CatalogListContent, CatalogListLoader } from '../../../widgets/catalogList';
import { CustomErrorBoundary } from '../../../widgets/customErrorBoundary';
import { Dialog, DialogWindow } from '../../../widgets/dialog';
import { EmptyList } from '../../../widgets/emptyList';
import { FormControl } from '../../../widgets/formControl';
import { SearchInput } from '../../../widgets/input';
import { Section } from '../../../widgets/section';
import { AdvancedSelect } from '../../../widgets/select';
import { damDialogPaginationQuery } from './__generated__/damDialogPaginationQuery.graphql';
import { damDialogQuery } from './__generated__/damDialogQuery.graphql';
import { damDialog_fragment$key } from './__generated__/damDialog_fragment.graphql';

const DialogDecorator = styled(DialogWindow)`
    width: 90%;
    height: 100%;
    flex-basis: unset;
`;

const Container = styled.div`
    height: 100%;
    width: 100%;
    background: white;
    position: relative;
`;

const LayoutContainer = styled.div`
    height: 100%;
    position: relative;
`;

const FormControlToolsContainers = styled.div`
    display: grid;
    grid-template-columns: 3fr 1fr;
    grid-gap: 20px;
    position: fixed;
    width: 73%;
    top: 12%;
    z-index: 10;
`;

interface DAMDialogProps {
    onClose: (productAssetUrl: string) => void;
    isMaterialReplacement?: boolean;
}

interface DAMSceneAsset {
    title: string;
    thumbnailUrl: string;
    fileUrl: string;
    assetType: string;
    roles: string[];
}

interface DAMScene {
    id: string;
    title: string;
    assets: DAMSceneAsset[];
}

const SectionTitle = styled.h3`
    font-size: 14px;
    font-weight: 600;
    margin: 20px 0 10px;
    color: ${(props) => props.theme.app.component.card.title.color};
`;

const damDialogFragment = graphql`
    fragment damDialog_fragment on Query
    @refetchable(queryName: "damDialogPaginationQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 20 }
        cursor: { type: "String", defaultValue: "0" }
        title: { type: "String", defaultValue: "" }
        filterMasks: { type: "String", defaultValue: "" }
        sortBy: { type: "String", defaultValue: "" }
    ) {
        damScenes(first: $count, after: $cursor, title: $title, filterMasks: $filterMasks, sortBy: $sortBy) @connection(key: "damDialog_damScenes") {
            __id # This is the cache id
            edges {
                node {
                    ... on DamScene {
                        id
                        title
                        assets {
                            roles
                            asset {
                                assetType
                                title
                                file {
                                    fileUrl
                                }
                                thumbnailUrl
                            }
                        }
                    }
                }
            }
        }
    }
`;

const DAMDialogProductsListContent: FC<{ handleSelectScene: (scene: DAMScene) => void }> = ({ handleSelectScene }) => {
    const [text, setText] = React.useState('');
    const [isPending, startTransition] = useTransition();
    const [filters, setFilters] = React.useState<string>('created_at:desc');

    const query = useLazyLoadQuery<damDialogQuery>(
        graphql`
            query damDialogQuery($filterMasks: String, $sortBy: String) {
                ...damDialog_fragment @arguments(filterMasks: $filterMasks, sortBy: $sortBy)
            }
        `,
        {
            filterMasks: 'published',
            sortBy: 'created_at:desc'
        },
        { fetchPolicy: 'network-only' }
    );

    const { data, loadNext, refetch, hasNext, isLoadingNext } = usePaginationFragment<damDialogPaginationQuery, damDialog_fragment$key>(
        damDialogFragment,
        query
    );

    const hasNoAssets = (data?.damScenes?.edges?.length ?? 0) === 0 && text.trim().length === 0;
    console.log('#DEBUG', data, data?.damScenes?.edges, text);

    if (hasNoAssets) {
        return (
            <LayoutContainer>
                <Section>
                    <EmptyList
                        icon={<NotFoundIcon />}
                        title={T._('No assets available in 3D DAM Asset Library', 'Admin')}
                        subTitle={T._('Upload assets to your 3D DAM Asset Library to make them available here.', 'Admin')}
                        hideContainer
                    />
                </Section>
            </LayoutContainer>
        );
    }

    return (
        <LayoutContainer>
            <Section>
                <FormControl>
                    <FormControlToolsContainers>
                        <SearchInput
                            fullWidth
                            placeholder={T._('Search assets...', 'Admin')}
                            onSearch={(filter: string) => {
                                setText(filter);
                                startTransition(() => {
                                    refetch(
                                        {
                                            title: filter,
                                            filterMasks: 'published' + (filter.trim() && filter.length > 0 ? ',title' : ''),
                                            sortBy: filters ? filters : ''
                                        },

                                        { fetchPolicy: 'network-only' }
                                    );
                                });
                            }}
                        />
                        <AdvancedSelect
                            options={[
                                { label: T._('A-Z', 'Admin'), value: 'title:asc' },
                                { label: T._('Z-A', 'Admin'), value: 'title:desc' },
                                { label: T._('most recent', 'Admin'), value: 'created_at:desc' },
                                { label: T._('less recent', 'Admin'), value: 'created_at:asc' }
                            ]}
                            defaultValue={{ label: T._('most recent', 'Admin'), value: 'created_at:desc' } as any}
                            placeholder={T._('Sort by...', 'Admin')}
                            onChange={(value: { value: string }) => {
                                setFilters(value?.value ?? '');
                                startTransition(() => {
                                    refetch(
                                        {
                                            title: text,
                                            filterMasks: 'published' + (text.trim() && text.length > 0 ? ',title' : ''),
                                            sortBy: value ? value.value : ''
                                        },
                                        { fetchPolicy: 'network-only' }
                                    );
                                });
                            }}
                        />
                    </FormControlToolsContainers>
                </FormControl>
                <div style={{ height: '40px' }}></div>
                {isPending ? (
                    CatalogListLoader
                ) : (
                    <CatalogListContent
                        noItemsIcon={<NotFoundIcon />}
                        noItemsMessage={T._('No asset was found', 'Admin')}
                        noItemsSubMessage={T._('Sorry, no asset was found for your search. Please try using different keywords.', 'Admin')}
                        models={
                            data.damScenes?.edges?.map((edge) => ({
                                id: edge.node.id!.toString(),
                                title: edge.node.title ?? 'N/D',
                                subtitle: '',
                                thumbnail: edge.node.assets?.[0]?.asset?.thumbnailUrl ?? '',
                                selected: false
                            })) ?? []
                        }
                        tooltipHtml={'This asset is not compatible with the Visual Product Customizer'}
                        isDamProductsCatalog
                        loadNext={() => loadNext(20)}
                        hasNext={hasNext}
                        isLoadingNext={isLoadingNext}
                        onItemClick={(item) => {
                            const scene = data!.damScenes!.edges!.find((edge) => edge.node.id === item.id);
                            handleSelectScene({
                                id: item.id,
                                title: item.title,
                                assets:
                                    scene?.node.assets?.map((a, index) => ({
                                        title: a.asset?.title ?? `Asset ${index + 1}`,
                                        thumbnailUrl: a.asset?.thumbnailUrl ?? '',
                                        fileUrl: a.asset?.file?.fileUrl ?? '',
                                        assetType: (a.asset?.assetType as string) ?? '',
                                        roles: [...(a.roles ?? [])] as string[]
                                    })) ?? []
                            });
                        }}
                    />
                )}
            </Section>
        </LayoutContainer>
    );
};

const DAMDialogProductAssetsListContent: FC<{
    scene: DAMScene;
    isMaterialReplacement: boolean;
    selectedAssetIndex: number;
    onSelectAsset(index: number): void;
    onBackClick(): void;
}> = ({ scene, isMaterialReplacement, selectedAssetIndex, onSelectAsset, onBackClick }) => {
    const meshAssets = scene.assets
        .map((asset, index) => ({ ...asset, originalIndex: index }))
        .filter(
            (a) =>
                a.assetType === 'ASSET_TYPE_MESH' ||
                a.roles.includes('SCENE_ASSET_ROLE_MODEL') ||
                (!a.assetType && !a.roles.some((r) => r === 'SCENE_ASSET_ROLE_MATERIAL'))
        );
    const materialAssets = scene.assets
        .map((asset, index) => ({ ...asset, originalIndex: index }))
        .filter((a) => a.assetType === 'ASSET_TYPE_MATERIAL' || a.roles.includes('SCENE_ASSET_ROLE_MATERIAL'));

    const assetsToShow = isMaterialReplacement ? materialAssets : meshAssets;
    const hasBothTypes = meshAssets.length > 0 && materialAssets.length > 0;

    return (
        <LayoutContainer>
            <Section>
                <FormControl>
                    <FormControlToolsContainers>
                        <BackButton onClick={() => onBackClick()} />
                    </FormControlToolsContainers>
                </FormControl>
                <div style={{ height: '40px' }}></div>

                {hasBothTypes && !isMaterialReplacement && <SectionTitle>{T._('Mesh', 'Admin')}</SectionTitle>}
                {hasBothTypes && isMaterialReplacement && <SectionTitle>{T._('Material', 'Admin')}</SectionTitle>}

                <CatalogListContent
                    models={assetsToShow.map((asset) => ({
                        id: asset.originalIndex.toString(),
                        title: asset.title,
                        subtitle: '',
                        thumbnail: asset.thumbnailUrl,
                        selected: selectedAssetIndex === asset.originalIndex
                    }))}
                    loadNext={() => {}}
                    hasNext={false}
                    isLoadingNext={false}
                    onItemClick={(model) => {
                        onSelectAsset(parseInt(model.id));
                    }}
                />
            </Section>
        </LayoutContainer>
    );
};

const DAMDialog: FC<DAMDialogProps> = ({ onClose, isMaterialReplacement }) => {
    const [selectedScene, setSelectedScene] = React.useState<DAMScene | null>(null);
    const [selectedAssetIndex, setSelectedAssetIndex] = React.useState<number>(-1);
    const preview3DStore = useContext(Preview3DStoreContext);

    const handleSelectScene = (scene: DAMScene) => {
        setSelectedScene(scene);
        setSelectedAssetIndex(scene.assets.length === 1 ? 0 : -1);
    };

    return (
        <Dialog
            title={T._('Select an asset from Asset Library', 'Admin')}
            windowDecorator={DialogDecorator}
            buttons={
                selectedScene && selectedAssetIndex >= 0
                    ? [
                          {
                              label: T._('Confirm', 'Admin'),
                              onClick: () => onClose(selectedScene.assets[selectedAssetIndex].fileUrl)
                          }
                      ]
                    : undefined
            }
        >
            <Container>
                <CustomErrorBoundary render={() => <ErrorMessage>{T._('Failed loading DAM assets.', 'Admin')}</ErrorMessage>}>
                    <Preview3DStoreContext.Provider value={preview3DStore}>
                        {!selectedScene && <DAMDialogProductsListContent handleSelectScene={handleSelectScene} />}
                        {selectedScene && (
                            <Preview3DStoreContext.Provider value={preview3DStore}>
                                <DAMDialogProductAssetsListContent
                                    onBackClick={() => {
                                        setSelectedScene(null);
                                        setSelectedAssetIndex(-1);
                                    }}
                                    scene={selectedScene}
                                    isMaterialReplacement={isMaterialReplacement ?? false}
                                    selectedAssetIndex={selectedAssetIndex}
                                    onSelectAsset={setSelectedAssetIndex}
                                />
                            </Preview3DStoreContext.Provider>
                        )}
                    </Preview3DStoreContext.Provider>
                </CustomErrorBoundary>
            </Container>
        </Dialog>
    );
};

export default DAMDialog;
